<template>
    <div :aria-labelledby="partyOptions.name" data-cy="salad-toppings" class="salad-toppings">
        <ion-card-title class="mx-8 mb-8 ion-text-center bold" data-cy="choose-dressing-title">Choose Your Dressing</ion-card-title>
        <div v-if="selectedSize?.isPartySize" class="mb-8" data-cy="choose-dressing-party-options">
            <radiolist :options="partyOptions" keyName="name" dense v-model="selectedPartyOption" class="mb-8" />
        </div>
        <div class="pl-24">
            <checklist
                :aria-labelledby="localDressing"
                v-if="!selectedSize?.isPartySize || selectedPartyOption == partyOptions[1]"
                :items="filteredLocalDressing"
                @update="localDressing = $event"
                onlyQuantity
                data-cy="choose-dressing-checklist"
                class="mb-button"
                @limit="limits"
            />
        </div>
        <toppingModal
            v-if="localToppings"
            showToppingPreview
            btnName="Customize"
            @update="localToppings = $event"
            :availableTopping="availableTopping"
            v-model:toppings="localToppings"
            hideSplitOpts
        />
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, ref, watch } from 'vue';
import { IonCardTitle } from '@ionic/vue';
import checklist from '@/components/menu/toppings/checklist.vue';
import baseToppings, { passThroughBindings } from '@/components/menu/toppings/baseToppings.vue';
import toppingModal from '@/components/menu/toppings/toppingModal.vue';
import radiolist from '@/components/inputs/radiolist.vue';
import { dressingVariety } from '@/services/orderService';

export default defineComponent({
    extends: baseToppings,
    components: { checklist, IonCardTitle, toppingModal, radiolist },
    emits: ['limits', 'blockSubmit'],
    setup(props, { emit }) {
        const { localDressing, localToppings } = passThroughBindings(props, emit);
        const minDressings = ref(10);

        const partyOptions = ref([{ name: 'Variety (' + minDressings.value + ' Packs)' }, { name: 'Choose My Own (up to ' + minDressings.value + ')' }, { name: 'No Dressing' }]);

        const defaultDressing = ref();
        const selectedPartyOption = ref();

        const filteredLocalDressing = computed(() => {
            return selectedPartyOption.value === (partyOptions.value?.[1] ?? null) ? localDressing.value || [] : localDressing.value || [];
        });

        watch(
            () => selectedPartyOption.value,
            (val) => {
                setTimeout(() => {
                    const filteredDressings = filteredLocalDressing.value.filter((d) => d?.name !== 'No Dressing');
                    let sumOfMultipliers;
                    sumOfMultipliers = filteredDressings.reduce((acc, d) => acc + (d?.multiplier || 0), 0);

                    val.name === 'Choose My Own (up to 10)' && sumOfMultipliers == 0 ? emit('limits', true) : emit('limits', false);
                });
            }
        );

        const limits = (e) => {
            emit('limits', e);
        };

        nextTick(() => {
            const noDressingItem = props.availableTopping?.dressings.find((item) => item.name === 'No Dressing');
            const filteredDressing = props.availableTopping?.dressings?.filter((d) => d?.name !== 'No Dressing');
            if (noDressingItem?.multiplier == 10) {
                //@ts-ignore
                selectedPartyOption.value = partyOptions.value[2];
            } else {
                selectedPartyOption.value = partyOptions.value[1];
                //@ts-ignore
                const sumOfMultiplier = filteredDressing.reduce((acc, d) => acc + (d?.multiplier || 0), 0);
                sumOfMultiplier == 0 ? emit('limits', true) : emit('limits', false);
            }

            // Initial selection
            localDressing.value = props.availableTopping?.dressings ?? [];
            localToppings.value = props.availableTopping?.toppings;
            defaultDressing.value = props.availableTopping?.dressings?.find((d) => d.selected);
            minDressings.value = props.availableTopping?.dressings[0]?.min ?? 10;
            autoSelectDressings();
        });

        const clearDressings = () => {
            return new Promise((resolve) => {
                if (!localDressing.value) resolve(true);
                localDressing.value.forEach((dressing, i) => {
                    dressing.selected = false;
                    dressing.multiplier = 0;
                    if (i + 1 >= localDressing.value.length) resolve(true);
                });
            });
        };

        const reselectMain = () => {
            const mainDressing = localDressing?.value?.find((d) => d.toppingPid == defaultDressing.value?.toppingPid);
            if (mainDressing) {
                mainDressing.multiplier = 1;
                mainDressing.selected = true;
            }
        };

        const autoSelectDressings = async () => {
            await clearDressings();
            if (selectedPartyOption.value == partyOptions.value[0] && props.selectedSize?.isPartySize) {
                dressingVariety(localDressing.value, defaultDressing.value);
            }
        };

        watch(selectedPartyOption, () => {
            emit('blockSubmit', true);
            const isPartyOption2 = selectedPartyOption.value === (partyOptions.value?.[2] ?? null);
            if (isPartyOption2) {
                localDressing.value.forEach((dressing) => {
                    dressing.selected = dressing.name === 'No Dressing';
                    dressing.multiplier = dressing.selected ? 10 : 0;
                });
            } else {
                autoSelectDressings().then(() => emit('blockSubmit', false));
            }
        });

        watch(
            () => props.selectedSize,
            async (size) => {
                if (size?.isPartySize) autoSelectDressings();
                else {
                    // Reselect default dressing when toggling back to entre size
                    await clearDressings();
                    reselectMain();
                }
            }
        );

        nextTick(() => reselectMain());
        return { localDressing, localToppings, partyOptions, selectedPartyOption, filteredLocalDressing, limits };
    },
});
</script>

<style scoped>
.salad-toppings {
    margin-bottom: 60px;
    padding-left: 8px;
}
</style>
